import { observable, action, computed, makeObservable } from "mobx";
import _ from "lodash";
import moment from "moment";
import apiActions from "api/actions";

function getInitialQueryData() {
  const start = moment().add(-1, "months");
  const end = moment().add(2, "months");
  const range = moment.range(start, end);

  return Array.from(range.by("month")).map((month) => ({
    params: { year: month.format("YYYY"), month: month.format("MM") },
    key: month.format("YYYY-MM"),
  }));
}

export class UlcEvent {
  constructor(data, root) {
    this._root = root;
    this.id = data.id;
    this.date = data.date;

    this.end_time = data.end_time;
    this.start_time = data.start_time;
    this.status = data.status;
    this.location = data.location;
    this.learning_center_id = data.learning_center_id;
    this.parent_id = data.parent && data.parent.id;
    this.parent_email = data.parent && data.parent.email;
    this.parent_first_name = data.parent && data.parent.first_name;
    this.parent_last_name = data.parent && data.parent.last_name;
    this.user_ids = data.user_ids || [];
    this.potential_children_ids = data.potential_children_ids || [];
  }

  get parent() {
    return this._root.ParentStore.find(this.parent_id) || this._root.ClientStore.find(this.parent_id);
  }

  get users() {
    const potentialChildren = this.potential_children_ids.map((id) =>
      this._root.PotentialChildStore.find(id)
    );
    const users = this.user_ids.map((id) => this._root.UserStore.find(id));
    return _.compact(users.concat(potentialChildren));
  }

  get localStart() {
    return this.startMoment.local();
  }

  get startMoment() {
    return moment.utc(`${this.date} ${this.start_time}`, "YYYY-MM-DD hh:mm");
  }

  get localEnd() {
    return this.endMoment.local();
  }

  get endMoment() {
    const naiveUtcEnd = moment.utc(
      `${this.date} ${this.end_time}`,
      "YYYY-MM-DD hh:mm"
    );
    if (this.startMoment.isAfter(naiveUtcEnd)) {
      // End time is next UTC date, so we need to shift from there.
      return naiveUtcEnd.add(1, "day").local();
    }
    // End time is same UTC date, so it doesn't matter.
    return naiveUtcEnd.local();
  }

  get shouldShowZoomLink() {
    return (
      moment.utc().isBefore(this.endMoment) &&
        moment.utc().isAfter(this.startMoment.clone().subtract(30, 'minutes'))
    );
  }

  get isInPast() {
    return this.endMoment.isBefore(moment.utc());
  }

  get requiresAction() {
    return (
      this.status === "pending" ||
      (this.status === "scheduled" && this.localEnd.isBefore(moment()))
    );
  }

  get creditsUsed() {
    if (this.status === "cancelled") {
      return 0;
    }
    return moment.duration(this.endMoment.diff(this.startMoment)).as("hours");
  }

  get statusAsUser() {
    return this._root.sessionStore.parent.id ? this.statusAsParent : this.statusAsInstructor;
  }

  get statusAsParent() {
    if (this.isInPast) {
      switch (this.status) {
        case 'scheduled':
          return {
            display: 'Pending Completion',
            raw: this.status,
            actionRequired: true,
          };
        case 'cancelled':
          return {
            display: 'Cancelled',
            raw: this.status,
            actionRequired: false,
          };
        case 'completed':
          return {
            display: 'Completed',
            raw: this.status,
            actionRequired: false,
          };
        default:
          return {
            display: 'Pending Confirmation',
            raw: this.status,
            actionRequired: true,
          };
      }
    } else {
      switch (this.status) {
        case 'scheduled':
          return {
            display: 'Scheduled',
            raw: this.status,
            actionRequired: false,
          };
        case 'cancelled':
          return {
            display: 'Cancelled',
            raw: this.status,
            actionRequired: false,
          };
        case 'completed':
          return {
            display: 'Completed',
            raw: this.status,
            actionRequired: false,
          }
        default:
          return {
            display: 'Pending Confirmation',
            raw: this.status,
            actionRequired: false,
          };
      }
    }
  }

  get statusAsInstructor() {
    if (this.isInPast) {
      switch (this.status) {
        case 'scheduled':
          return {
            display: 'Pending Completion',
            raw: this.status,
            actionRequired: true,
          };
        case 'cancelled':
          return {
            display: 'Cancelled',
            raw: this.status,
            actionRequired: false,
          };
        case 'completed':
          return {
            display: 'Completed',
            raw: this.status,
            actionRequired: false,
          };
        default:
          return {
            display: 'Pending Confirmation',
            raw: this.status,
            actionRequired: true,
          };
      }
    } else {
      switch (this.status) {
        case 'scheduled':
          return {
            display: 'Scheduled',
            raw: this.status,
            actionRequired: false,
          };
        case 'cancelled':
          return {
            display: 'Cancelled',
            raw: this.status,
            actionRequired: false,
          };
        case 'completed':
          return {
            display: 'Completed',
            raw: this.status,
            actionRequired: false,
          };
        default:
          return {
            display: 'Pending Confirmation',
            raw: this.status,
            actionRequired: true,
          };
      }
    }
  }

  get instructorInternalComments() {
    return this._root.InstructorInternalCommentStore.forEvent(this.id);
  }
}

export default class UlcEventStore {
  constructor(root) {
    makeObservable(this, {
      data: observable,
      requests: observable,
      add: action,
      addMany: action,
      remove: action,
      all: computed,
      reset: action,
      fetchAll: action,
      fetchById: action,
      refetch: action,
      fetchAllCompleted: computed,
      groupedByStatusAll: computed,
    });

    this.root = root;
  }

  data = new Map();
  requests = new Map();

  add = (data) => {
    const track = new UlcEvent(data, this.root);
    this.data.set(track.id, track);
  };

  addMany = (datas) => {
    const merge = new Map();
    datas.forEach((data) => {
      const ulcEvent = new UlcEvent(data, this.root);
      merge.set(ulcEvent.id, ulcEvent);
    });
    this.data.merge(merge);
  };

  remove = (id) => {
    this.data.delete(id);
  };

  find(id) {
    return this.data.get(id);
  }

  get all() {
    return Array.from(this.data.values());
  }

  get groupedByStatusAll() {
    return _.groupBy(Array.from(this.data.values()), "status");
  }

  reset = () => {
    this.data.clear();
    this.requests.clear();
  };

  fetchInitial() {
    const queryData = getInitialQueryData();
    const requests = [];

    queryData.forEach(({ params, key }) => {
      if (this.requests.get(key) === undefined) {
        this.requests.set(key, "pending");
        requests.push(
          apiActions.index("events", params).then((response) => {
            this.addMany(response.data);
            this.requests.set(key, "complete");
          })
        );
      }
    });

    return Promise.all(requests);
  }

  fetchAll() {
    if (this.requests.get("all") === undefined) {
      this.requests.set("all", "pending");
      return apiActions.index("events").then((response) => {
        this.addMany(response.data);
        this.requests.set("all", "complete");
      });
    }
    return Promise.resolve();
  }

  fetchById(id) {
    if (
      this.requests.get("all") === undefined &&
      this.requests.get(id) === undefined
    ) {
      this.requests.set(id, "pending");
      return apiActions.get("events", id).then((response) => {
        this.add(response.data);
        this.requests.set(id, "complete");
        return this.find(id);
      });
    }
    return Promise.resolve(this.find(id));
  }

  refetch() {
    if (this.requests.get("all") !== undefined) {
      this.reset();
      return this.fetchAll();
    } else if (this.fetchInitialCompleted) {
      this.reset();
      return this.fetchInitial();
    }
    const keys = Array.from(this.requests.keys());
    this.reset();
    return Promise.all(
      keys.map((key) => {
        return this.fetchById(key);
      })
    );
  }

  get fetchInitialCompleted() {
    const queryData = getInitialQueryData();
    return _.every(
      queryData,
      ({ key }) => this.requests.get(key) === "complete"
    );
  }

  get fetchAllCompleted() {
    return this.requests.get("all") === "complete";
  }

  fetchByIdCompleted(id) {
    return this.fetchAllCompleted || this.requests.get(id) === "complete";
  }

  isFirstEvent(parentId) {
    return (
      Array.from(this.data.values()).filter(
        (event) => event.parent_id === parentId
      ).length === 1
    );
  }

  hasNoEvents(parentId) {
    return (
      Array.from(this.data.values()).filter(
        (event) => event.parent_id === parentId
      ).length === 0
    );
  }

  creditsUsedForParent(parentId) {
    return Array.from(this.data.values())
      .filter((event) => event.parent_id === parentId)
      .map((event) => event.creditsUsed)
      .reduce((acc, hours) => acc + hours, 0);
  }

  creditsUsedForParentInUlc(parentId, learningCenterId) {
    return Array.from(this.data.values())
      .filter(
        (event) =>
          event.parent_id === parentId &&
          event.learning_center_id === learningCenterId
      )
      .map((event) => event.creditsUsed)
      .reduce((acc, hours) => acc + hours, 0);
  }

  futureEventsForParent(parentId) {
    return Array.from(this.data.values())
      .filter((event) => event.parent_id === parentId)
      .filter((event) => !event.isInPast)
      .sort((a, b) => (a.startMoment.isBefore(b.startMoment) ? -1 : 1));
  }

  pastEventsForParent(parentId) {
    return Array.from(this.data.values())
      .filter((event) => event.parent_id === parentId)
      .filter((event) => event.isInPast)
      .sort((a, b) => (b.startMoment.isBefore(a.startMoment) ? -1 : 1));
  }

  futureEventsForChild(parentId, childUserId) {
    return this.futureEventsForParent(parentId).filter((event) =>
      _.includes(event.user_ids, childUserId)
    );
  }

  futureEventsForPotentialChild(parentId, potentialChildUserId) {
    return this.futureEventsForParent(parentId).filter((event) =>
      _.includes(event.potential_children_ids, potentialChildUserId)
    );
  }

  eventsForDate = (date) => {
    return this.all.filter((event) => event.localStart.isSame(date, "day"));
  }

  pastEventsForChildInULC(childId, ulcId) {
    const potentialChild = this.root.PotentialChildStore.find(childId);
    const events = _.chain(this.all)
                    .filter(event => event.isInPast)
                    .filter(['learning_center_id', ulcId])
                    .filter(
                      event =>
                      event.potential_children_ids.includes(potentialChild.id) ||
                        (potentialChild.user_id &&
                         event.user_ids.includes(potentialChild.user_id)),
                    )
                    .sortBy(['startMoment'])
                    .value();
    return events;
  }

  futureEventsForChildInULC(childId, ulcId) {
    const potentialChild = this.root.PotentialChildStore.find(childId);
    const events = _.chain(this.all)
                    .filter(event => !event.isInPast)
                    .filter(['learning_center_id', ulcId])
                    .filter(
                      event =>
                      event.potential_children_ids.includes(potentialChild.id) ||
                        (potentialChild.user_id &&
                         event.user_ids.includes(potentialChild.user_id)),
                    )
                    .sortBy(['startMoment'])
                    .value();
    return events;
  }

  /**
   * Submit completion feedback from an instructor regarding a learning session.
   *
   * @param {UlcEvent} event - The learning session event to provide feedback for. Context data is gathered from here.
   * @param {string} message - The text of the message from the instructor to the parent to be included.
   * @param {string} internalComment - The text of an internal comment from the instructor not shared with the parent.
   * @param {object} lessonProgressLogs - Basic JS objects for creating new lesson progress logs.
   * @param {boolean} sendEmail - Should a notification email be sent to the parent? Defaults to true.
   * @returns {Promise} Resolves when all requests complete, rejects if they failed.
   **/
  submitCompletionFeedback(event, message, internalComment, lessonProgressLogs, sendEmail = true) {
    return Promise.all([
      this.root.ClientStore.fetchAll(),
      this.root.ParentStore.fetchAll(),
      this.root.PotentialChildStore.fetchAll(),
      this.root.TopicStore.fetchAll(),
    ]).then(() => {
      const parent = event.parent
      const potentialChild = event.potential_children_ids.length > 0 ? this.root.PotentialChildStore.find(event.potential_children_ids[0]) : this.root.PotentialChildStore.forUser(event.user_ids[0])
      return apiActions
        .rawPatch(`events/${event.id}`, { id: event.id, status: 'completed', send_email: sendEmail })
        .then(() => {
          const requestQueue = [];
          if (message) {
            requestQueue.push(
              this.root.UserMessageStore.sendMessage(
                parent.user_id,
                message,
                null,
                event.id
              )
            )
          }
          if (internalComment) {
            event.user_ids.forEach(userId => {
              requestQueue.push(
                this.root.InstructorInternalCommentStore.create({
                  comment: internalComment,
                  event_id: event.id,
                  student_id: userId
                })
              )
            })
            event.potential_children_ids.forEach(childId => {
              requestQueue.push(
                this.root.InstructorInternalCommentStore.create({
                  comment: internalComment,
                  event_id: event.id,
                  potential_child_id: childId
                })
              )
            })
          }
          if (lessonProgressLogs.length > 0) {
            event.user_ids.forEach(userId => {
              lessonProgressLogs.forEach(log => {
                const lesson = this.root.TopicStore.findLesson(log.lessonId)
                const createRequest = apiActions.rawPost(`users/${userId}/lesson_progress_logs`, {
                  lesson_progress_log: {
                    lesson_id: log.lessonId,
                    feels_mastered: log.feelsMastered,
                    comment: log.comment
                  }
                })
                requestQueue.push(createRequest)
                if (log.comment.length > 0) {
                  const message = `
COMMENT ON LESSON ${lesson.title} FOR ${potentialChild.first_name}:
${log.comment}`;
                  requestQueue.push(this.root.UserMessageStore.sendMessage(parent.user_id, message))
                }
              })
            })
          }
          return Promise.all(requestQueue)
        })
    })
  }
}
